import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import { getRolePermissionsMatrix } from "../../../../utils/services/control/rbacAdminService";
import {
  getUsers,
  getEmployeeInfo,
} from "../../../../utils/services/control/masterDataService";

/**
 * Dialog read-only que muestra:
 *  - Tab "Modules": módulos a los que el rol tiene acceso (matrix.assigned=true).
 *  - Tab "Employees": empleados con cuenta que tienen este rol asignado.
 *
 * El backend no expone un endpoint "users by role", así que la lista de
 * empleados se compone llamando getUsers() + getEmployeeInfo(employeeId) en
 * paralelo y filtrando los que tienen el roleId que nos interesa. Para
 * organizaciones de cientos de empleados esto puede ser N+1 pesado; si llega
 * a doler, conviene pedir un endpoint dedicado al backend.
 */
const RoleDetailDialog = ({ open, onClose, role }) => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [moduleAccess, setModuleAccess] = useState([]);
  const [employees, setEmployees] = useState([]);

  const loadData = useCallback(async () => {
    if (!role?.roleId) return;
    setLoading(true);
    setError("");
    try {
      // Lanzamos las dos cargas en paralelo. La de empleados es N+1 (un
      // getEmployeeInfo por usuario), por eso se promesa todo junto.
      const [matrixRes, users] = await Promise.all([
        getRolePermissionsMatrix(role.roleId),
        getUsers(),
      ]);

      // Modules con acceso (assigned=true) — agrupados por moduleId para
      // no duplicar en caso de varias filas del mismo módulo.
      const modulesMap = new Map();
      if (matrixRes?.result === 0 && Array.isArray(matrixRes.matrix)) {
        matrixRes.matrix
          .filter((c) => c.assigned)
          .forEach((c) => {
            if (!modulesMap.has(c.moduleId)) {
              modulesMap.set(c.moduleId, {
                moduleId: c.moduleId,
                moduleName: c.moduleName,
                moduleKey: c.moduleKey,
              });
            }
          });
      }
      setModuleAccess(Array.from(modulesMap.values()));

      // Empleados con este rol: getEmployeeInfo retorna 1 fila por rol.
      // Pedimos el info de todos los users en paralelo y filtramos los que
      // tienen el roleId que buscamos.
      const userInfos = await Promise.all(
        (users || []).map((u) =>
          getEmployeeInfo(u.employeeId)
            .then((info) => ({ user: u, info }))
            .catch(() => ({ user: u, info: null }))
        )
      );
      const matched = userInfos
        .filter(({ info }) =>
          (Array.isArray(info) ? info : [info]).some(
            (row) => row?.roleId === role.roleId
          )
        )
        .map(({ user }) => user);
      setEmployees(matched);
    } catch (err) {
      setError(err?.response?.data?.message || "Error loading role detail");
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    if (open && role) {
      setTab(0);
      loadData();
    } else {
      setModuleAccess([]);
      setEmployees([]);
      setError("");
    }
  }, [open, role, loadData]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="h6" component="span">
            Role Detail — {role?.roleName}
          </Typography>
          {role?.isSystem && (
            <Chip label="System" size="small" color="warning" />
          )}
          <Chip
            label={role?.roleKey}
            size="small"
            variant="outlined"
            sx={{ fontFamily: "monospace" }}
          />
        </Box>
        {role?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {role.description}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab
            icon={<ViewModuleOutlinedIcon />}
            iconPosition="start"
            label={`Modules (${moduleAccess.length})`}
          />
          <Tab
            icon={<PeopleOutlinedIcon />}
            iconPosition="start"
            label={`Employees (${employees.length})`}
          />
        </Tabs>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : tab === 0 ? (
          // MODULES tab
          moduleAccess.length === 0 ? (
            <Alert severity="info">
              This role has no module access assigned yet.
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>
                    <TableCell sx={{ fontWeight: 600, width: 220 }}>
                      Module Key
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...moduleAccess]
                    .sort((a, b) =>
                      (a.moduleName || "").localeCompare(b.moduleName || "")
                    )
                    .map((m) => (
                      <TableRow key={m.moduleId} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>
                            {m.moduleName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="caption"
                            fontFamily="monospace"
                            color="text.secondary"
                          >
                            {m.moduleKey}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )
        ) : // EMPLOYEES tab
        employees.length === 0 ? (
          <Alert severity="info">No employees have this role assigned.</Alert>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600, width: 140 }}>
                    Position
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...employees]
                  .sort((a, b) =>
                    `${a.firstName} ${a.lastName}`.localeCompare(
                      `${b.firstName} ${b.lastName}`
                    )
                  )
                  .map((e) => (
                    <TableRow key={e.employeeId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {e.firstName} {e.lastName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {e.businessEmail || e.mail || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {e.jobPositionName || "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleDetailDialog;
